import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import {
  Activity, BellRing, BookOpenText, Building2, CircleGauge, Database, Flame,
  LogOut, Menu, Newspaper, Radio, SearchCheck, Send, Settings2, ShieldCheck, Users, X,
} from 'lucide-react'
import { api } from '../lib/api'
import { useArticleAlerts } from '../lib/alerts'
import { useAuth } from '../lib/auth'
import { navigate, usePathname } from '../lib/route'
import { useStream } from '../lib/stream'
import { Badge, Button } from './ui'
import { LiveTimestamp } from './time'

type NavItem = { label: string; path: string; icon: ComponentType<{ size?: number; strokeWidth?: number }> }

const primary: NavItem[] = [
  { label: 'Live', path: '/live', icon: Radio },
  { label: 'Events', path: '/events', icon: Activity },
  { label: 'Stories', path: '/stories', icon: Newspaper },
  { label: 'Sources', path: '/sources', icon: Building2 },
  { label: 'Telegram', path: '/telegram', icon: Send },
  { label: 'Indexing', path: '/indexing', icon: SearchCheck },
  { label: 'System', path: '/system', icon: CircleGauge },
]

function NavLink({ item, close }: { item: NavItem; close: () => void }) {
  const pathname = usePathname()
  const active = pathname === item.path || pathname.startsWith(`${item.path}/`)
  const Icon = item.icon
  return (
    <a
      href={item.path}
      aria-current={active ? 'page' : undefined}
      data-state={active ? 'active' : 'inactive'}
      className="nav-link"
      onClick={(event) => { event.preventDefault(); navigate(item.path); close() }}
    >
      <Icon size={17} strokeWidth={1.8} aria-hidden="true" /><span>{item.label}</span>
      {item.path === '/live' ? <span className="nav-link__pulse" aria-label="Live" /> : null}
    </a>
  )
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, refresh } = useAuth()
  const { connected } = useStream()
  const articleAlerts = useArticleAlerts()
  const [menuOpen, setMenuOpen] = useState(false)
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false)
  const [clock, setClock] = useState(() => new Date())
  useEffect(() => {
    const interval = window.setInterval(() => setClock(new Date()), 1000)
    return () => window.clearInterval(interval)
  }, [])
  const close = () => setMenuOpen(false)
  const logout = async () => {
    await api('/v1/auth/logout', { method: 'POST' })
    await refresh()
  }
  return (
    <div className="app-shell">
      {articleAlerts.alert ? <div className="breaking-alert" role="alert"><span className="breaking-alert__flag">NEW</span><strong>{articleAlerts.alert.headline}</strong><LiveTimestamp value={articleAlerts.alert.publishedAt || articleAlerts.alert.receivedAt.toISOString()} compact />{articleAlerts.alert.url ? <a href={articleAlerts.alert.url} target="_blank" rel="noreferrer">Open article</a> : null}<button onClick={articleAlerts.dismiss} aria-label="Dismiss new article alert"><X size={15} /></button></div> : null}
      <header className="mobile-header">
        <a href="/live" className="wordmark wordmark--mobile" onClick={(event) => { event.preventDefault(); navigate('/live') }}><Flame aria-hidden="true" /> BLAZE</a>
        <Button variant="ghost" size="icon" aria-label={menuOpen ? 'Close menu' : 'Open menu'} onClick={() => setMenuOpen((open) => !open)}>{menuOpen ? <X /> : <Menu />}</Button>
      </header>
      <aside className="sidebar" data-state={menuOpen ? 'open' : 'closed'}>
        <div className="sidebar__brand">
          <a href="/live" className="wordmark" onClick={(event) => { event.preventDefault(); navigate('/live'); close() }}><Flame aria-hidden="true" /><span>BLAZE</span></a>
          <p>News intelligence<br />operations desk</p>
        </div>
        <nav aria-label="Primary navigation" className="sidebar__nav">
          <span className="nav-label">Intelligence</span>
          {primary.map((item) => <NavLink key={item.path} item={item} close={close} />)}
          {user?.role === 'ADMIN' ? (
            <>
              <span className="nav-label nav-label--spaced">Control</span>
              <NavLink item={{ label: 'Admin', path: '/admin', icon: ShieldCheck }} close={close} />
            </>
          ) : null}
        </nav>
        <div className="sidebar__footer">
          <div className="operator">
            <span className="operator__avatar">{user?.username.slice(0, 2).toUpperCase()}</span>
            <span><strong>{user?.username}</strong><small>{user?.role === 'ADMIN' ? 'Administrator' : 'Analyst'}</small></span>
            <Button variant="ghost" size="icon" aria-label="Sign out" onClick={() => void logout()}><LogOut size={17} /></Button>
          </div>
        </div>
      </aside>
      {menuOpen ? <button className="sidebar-scrim" aria-label="Close navigation" onClick={close} /> : null}
      <main className="workspace">
        <div className="status-rail" aria-label="System status">
          <Badge tone={connected ? 'good' : 'warn'} dot>{connected ? 'Realtime linked' : 'Reconnecting'}</Badge>
          <span className="status-rail__divider" />
          <span className="status-rail__time">{clock.toLocaleTimeString([], { hour12: false })}<small> IST</small></span>
          <button className="icon-button alert-toggle" data-state={articleAlerts.enabled ? 'enabled' : 'disabled'} aria-label={articleAlerts.enabled ? 'Article alerts enabled' : 'Enable article alerts'} title={articleAlerts.enabled ? 'Article alerts enabled' : 'Enable article alerts'} onClick={() => void articleAlerts.enable()}><BellRing size={16} />{articleAlerts.unread ? <span>{articleAlerts.unread}</span> : null}</button>
          <button className="icon-button" aria-label="Alert preferences" aria-expanded={alertSettingsOpen} onClick={() => setAlertSettingsOpen((open) => !open)}><Settings2 size={16} /></button>
          {alertSettingsOpen ? <div className="alert-settings" role="dialog" aria-label="New article alert preferences"><div><span className="panel-kicker">New article alerts</span><strong>{articleAlerts.enabled ? 'Sound enabled' : 'Sound not enabled'}</strong><small>Browser permission: {articleAlerts.permission}</small></div><p>A short notification tone only—never voice. Testing also requests browser notification permission.</p><label><span>Highlight rule</span><input value={articleAlerts.filterExpression} onChange={(event) => articleAlerts.setFilterExpression(event.target.value)} placeholder="election AND India NOR cricket OR Gaza" /></label><label><span>Fuzzy similarity · {Math.round(articleAlerts.similarityThreshold * 100)}%</span><input type="range" min="0" max="1" step="0.05" value={articleAlerts.similarityThreshold} onChange={(event) => articleAlerts.setSimilarityThreshold(Number(event.target.value))} /></label><small>AND requires terms · OR allows alternatives · NOT/NOR exclude the next term. Quoted phrases stay together. Blank means every genuinely new article.</small><div className="row-actions"><Button variant="ghost" size="small" onClick={() => void articleAlerts.enable()}>Enable</Button><Button size="small" onClick={() => void articleAlerts.test()}>Test alert</Button></div></div> : null}
        </div>
        <div className="workspace__content">{children}</div>
      </main>
    </div>
  )
}

export const sectionIcons = { BookOpenText, Database, Users }
